import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { UsageCard } from "@/components/UsageMeter";
import { categoryColor, CHART_COLORS } from "@/lib/categories";
import { formatMoney, monthLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { ScanLine, ReceiptText, CreditCard, BellRing, ArrowUpRight } from "lucide-react";

function KpiCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="card-base p-4 md:p-5" data-testid="kpi-card">
      <div className="flex items-start justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}1a`, border: `1px solid ${accent}40` }}>
          <Icon style={{ width: 16, height: 16, color: accent }} />
        </span>
      </div>
      <p className="text-2xl md:text-3xl font-display font-semibold tabular-nums mt-3">{value}</p>
    </div>
  );
}

function urgencyStyle(u) {
  const map = {
    overdue: { bg: "var(--danger-bg)", fg: "var(--danger-fg)", bd: "var(--danger-border)" },
    warning: { bg: "var(--warning-bg)", fg: "var(--warning-fg)", bd: "var(--warning-border)" },
    info: { bg: "var(--info-bg)", fg: "var(--info-fg)", bd: "var(--info-border)" },
    ok: { bg: "rgba(255,255,255,0.05)", fg: "#9aa4b2", bd: "rgba(255,255,255,0.1)" },
  };
  return map[u] || map.ok;
}

// Category-colored tooltip for the "By category" donut. Uses the active
// category's colour as the background with dark, high-contrast text so it is
// clearly readable on mobile (e.g. "Spo\u017cywcze: 178,89 z\u0142").
function CategoryTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const item = p.payload || {};
  const color = categoryColor(item.category) || "#22D3EE";
  return (
    <div
      data-testid="category-tooltip"
      style={{
        background: color,
        color: "#0B1018",
        borderRadius: 10,
        padding: "9px 13px",
        fontWeight: 700,
        fontSize: 13,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        border: "1px solid rgba(0,0,0,0.2)",
        boxShadow: `0 10px 26px rgba(0,0,0,0.5), 0 0 0 3px ${color}33`,
      }}
    >
      {item.label}: {formatMoney(p.value)}
    </div>
  );
}


export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tc } = useI18n();
  const ctx = useOutletContext();
  const [summary, setSummary] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.summary(), api.usage()])
      .then(([s, u]) => { setSummary(s.data); setUsage(u.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "";

  const renewBadge = (days) => {
    if (days < 0) return t("renewal.overdue", { n: Math.abs(days) });
    if (days === 0) return t("renewal.today");
    return t("renewal.inDays", { n: days });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-page">
        <Skeleton className="h-8 w-64 bg-white/5" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0,1,2,3].map((i) => <Skeleton key={i} className="h-28 bg-white/5" />)}
        </div>
        <Skeleton className="h-72 bg-white/5" />
      </div>
    );
  }

  const trend = (summary?.monthly_trend || []).map((tr) => ({ ...tr, label: monthLabel(tr.month) }));
  const breakdown = (summary?.category_breakdown || []).map((b) => ({ ...b, label: tc(b.category) }));
  const hasData = (summary?.receipts_count || 0) > 0 || (summary?.subscriptions_count || 0) > 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{t("dashboard.welcome", { name: firstName })}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("dashboard.subtitle")}</p>
        </div>
        <Button data-testid="dashboard-scan-button" onClick={() => navigate("/scan")} className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
          <ScanLine style={{ width: 18, height: 18 }} className="mr-2" /> {t("dashboard.scanReceipt")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={t("kpi.thisMonth")} value={formatMoney(summary?.month_total)} icon={ReceiptText} accent="#22D3EE" />
        <KpiCard label={t("kpi.receipts")} value={summary?.receipts_count ?? 0} icon={ScanLine} accent="#7CF7C2" />
        <KpiCard label={t("kpi.subscriptions")} value={summary?.subscriptions_count ?? 0} icon={CreditCard} accent="#82AAFF" />
        <KpiCard label={t("kpi.renewals")} value={summary?.upcoming_renewals_count ?? 0} icon={BellRing} accent="#FFD38A" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Spend trend */}
        <div className="lg:col-span-2 card-base p-5" data-testid="monthly-spend-chart">
          <h3 className="font-display font-semibold mb-4">{t("dashboard.monthlySpend")}</h3>
          {hasData ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trend} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }} axisLine={false} tickLine={false} width={50} />
                <Tooltip
                  contentStyle={{ background: "var(--surface-2)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff" }}
                  formatter={(v) => [formatMoney(v), t("chart.spend")]}
                />
                <Area type="monotone" dataKey="total" stroke="#22D3EE" strokeWidth={2} fill="url(#spendFill)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart onScan={() => navigate("/scan")} t={t} />
          )}
        </div>

        {/* Category breakdown */}
        <div className="card-base p-5" data-testid="category-breakdown-chart">
          <h3 className="font-display font-semibold mb-4">{t("dashboard.byCategory")}</h3>
          {breakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={breakdown} dataKey="total" nameKey="label" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="none" isAnimationActive={false}>
                    {breakdown.map((b, i) => <Cell key={i} fill={categoryColor(b.category) || CHART_COLORS[i % CHART_COLORS.length]} stroke="none" />)}
                  </Pie>
                  <Tooltip content={<CategoryTooltip />} cursor={false} wrapperStyle={{ outline: "none" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5">
                {breakdown.slice(0, 5).map((b) => (
                  <div key={b.category} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: categoryColor(b.category) }} />{b.label}</span>
                    <span className="font-mono tabular-nums text-muted-foreground">{formatMoney(b.total)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-44 flex items-center justify-center text-sm text-muted-foreground text-center">{t("dashboard.noExpensesMonth")}</div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Renewal alerts */}
        <div className="lg:col-span-2 card-base p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><BellRing className="text-amber-300" style={{ width: 18, height: 18 }} /> {t("dashboard.upcomingRenewals")}</h3>
            <button onClick={() => navigate("/subscriptions")} className="text-sm text-cyan-300 hover:underline inline-flex items-center gap-1">{t("dashboard.manage")} <ArrowUpRight style={{ width: 14, height: 14 }} /></button>
          </div>
          {(summary?.upcoming_renewals || []).length > 0 ? (
            <div className="space-y-2">
              {summary.upcoming_renewals.map((s) => {
                const b = urgencyStyle(s.urgency);
                return (
                  <div key={s.id} className="flex items-center justify-between rounded-lg bg-white/4 px-3.5 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: categoryColor(s.category) }} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{formatMoney(s.amount, s.currency)}</p>
                      </div>
                    </div>
                    <span className="text-xs rounded-full px-2.5 py-1 shrink-0" style={{ background: b.bg, color: b.fg, border: `1px solid ${b.bd}` }}>
                      {renewBadge(s.days_until_renewal)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-32 flex flex-col items-center justify-center text-center">
              <p className="text-sm text-muted-foreground">{t("dashboard.noRenewals")}</p>
              <button onClick={() => navigate("/subscriptions")} className="text-sm text-cyan-300 hover:underline mt-2">{t("dashboard.addSubscription")}</button>
            </div>
          )}
        </div>

        <UsageCard usage={usage} onUpgrade={ctx?.openUpgrade} />
      </div>
    </div>
  );
}

function EmptyChart({ onScan, t }) {
  return (
    <div className="h-[260px] flex flex-col items-center justify-center text-center">
      <ScanLine className="text-muted-foreground mb-3" style={{ width: 28, height: 28 }} />
      <p className="text-sm text-muted-foreground">{t("dashboard.scanFirst")}</p>
      <button onClick={onScan} className="text-sm text-cyan-300 hover:underline mt-2">{t("dashboard.scanA")}</button>
    </div>
  );
}
